import * as React from 'react';
import { ChevronLeft, Layers } from 'lucide-react';
import { cn } from '@klareospace/pharos-lib-web-utils';
import { useTranslation } from 'react-i18next';
import { useMasterContext } from '&/master';
import { useSites } from '../../../settings/sites/hooks/use-sites';
import { SiteRow } from './site-row';

// ─── Props ────────────────────────────────────────────────────────────────────

interface SitesPanelProps {
  canReadSites: boolean;
  canSelectSpace: boolean;
  isSingleSpaceImposed: boolean;
  onBack: () => void;
  onClose: () => void;
  spaceId: string | null;
  spaceName: string | null;
}

// ─── Private helpers ──────────────────────────────────────────────────────────

interface SpaceHeaderProps {
  canSelectSpace: boolean;
  isSelected: boolean;
  isSingleSpaceImposed: boolean;
  onBack: () => void;
  onSelect: () => void;
  onSelectKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  spaceName: string | null;
}

function SpaceHeader({
  canSelectSpace,
  isSelected,
  isSingleSpaceImposed,
  onBack,
  onSelect,
  onSelectKeyDown,
  spaceName,
}: SpaceHeaderProps): React.JSX.Element {
  const { t } = useTranslation('common');

  if (!canSelectSpace) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 border-y border-border">
        {!isSingleSpaceImposed && (
          <button
            aria-label={t('mainSelector.back')}
            className="p-0.5 rounded hover:bg-accent transition-colors shrink-0 rtl:rotate-180"
            onClick={onBack}
            type="button"
          >
            <ChevronLeft className="size-4" />
          </button>
        )}
        <div className="flex items-center gap-2 flex-1 min-w-0 text-start">
          <Layers className="size-4 text-muted-foreground shrink-0" />
          <span className="text-sm font-medium truncate flex-1">{spaceName}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-3 py-2 border-y border-border transition-colors group/space-header cursor-pointer',
        isSelected ? 'bg-accent' : 'hover:bg-muted',
      )}
      onClick={onSelect}
      onKeyDown={onSelectKeyDown}
      role="button"
      tabIndex={0}
    >
      {/* Back button — hidden when space is imposed (single-space user) */}
      {!isSingleSpaceImposed && (
        <button
          aria-label={t('mainSelector.back')}
          className="p-0.5 rounded hover:bg-accent transition-colors shrink-0 rtl:rotate-180"
          onClick={(e) => {
            e.stopPropagation();
            onBack();
          }}
          onKeyDown={(e) => e.stopPropagation()}
          type="button"
        >
          <ChevronLeft className="size-4" />
        </button>
      )}

      <div className="flex items-center gap-2 flex-1 min-w-0 text-start">
        <Layers className="size-4 text-muted-foreground shrink-0" />
        <span className="text-sm font-medium truncate flex-1">{spaceName}</span>
      </div>

      {isSelected ? (
        <span className="text-[9px] font-semibold text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0">
          {t('mainSelector.current')}
        </span>
      ) : (
        <span className="text-[11px] text-primary font-medium hidden group-hover/space-header:inline shrink-0">
          {t('mainSelector.select')}
        </span>
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SitesPanel({
  canReadSites,
  canSelectSpace,
  isSingleSpaceImposed,
  onBack,
  onClose,
  spaceId,
  spaceName,
}: SitesPanelProps): React.JSX.Element {
  const { t } = useTranslation('common');
  const { selectSpace, siteId: currentSiteId, spaceId: currentSpaceId } = useMasterContext();
  const { data } = useSites({
    enabled: !!spaceId && canReadSites,
    page: 1,
    pageSize: 50,
    spaceIds: spaceId ? [spaceId] : [],
  });

  const sites = data?.items ?? [];
  const total = data?.total ?? 0;

  // Space is "active" only when it's the current space AND no site is selected
  const isSpaceOnlySelected = spaceId === currentSpaceId && !currentSiteId;

  const handleSpaceSelect = (): void => {
    if (spaceId) {
      selectSpace(spaceId);
      onClose();
    }
  };

  const handleSpaceSelectKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSpaceSelect();
    }
  };

  return (
    <div>
      {spaceName && (
        <SpaceHeader
          canSelectSpace={canSelectSpace}
          isSelected={isSpaceOnlySelected}
          isSingleSpaceImposed={isSingleSpaceImposed}
          onBack={onBack}
          onSelect={handleSpaceSelect}
          onSelectKeyDown={handleSpaceSelectKeyDown}
          spaceName={spaceName}
        />
      )}

      <div className="px-3 py-1.5">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {t('mainSelector.sites')} ({total})
        </span>
      </div>

      <div>
        <div className="p-1">
          {spaceId && sites.map((site) => <SiteRow key={site.id} onClose={onClose} site={site} spaceId={spaceId} />)}
        </div>
      </div>
    </div>
  );
}
